import React, { useEffect, useState } from 'react';
import { Card } from '@heroui/react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  SALES_DASHBOARD_PATH,
  normalizeSalesJobId,
} from '../../constants/salesDashboard';
import { useSalesJobDetails } from '../../hooks/sales/useSalesJobDetails';
import SalesJobDetailsPanel from './SalesJobDetailsPanel';

const getQueryErrorMessage = (error: unknown) => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'data' in error.response &&
    typeof error.response.data === 'object' &&
    error.response.data !== null &&
    'error' in error.response.data &&
    typeof error.response.data.error === 'string'
  ) {
    return error.response.data.error;
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  return 'Unable to fetch job details.';
};

const SalesJobLookupPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedDisplayJobId = normalizeSalesJobId(
    searchParams.get('jobId') || searchParams.get('displayJobId') || '',
  );
  const [inputValue, setInputValue] = useState(requestedDisplayJobId);

  useEffect(() => {
    setInputValue(requestedDisplayJobId);
  }, [requestedDisplayJobId]);

  const {
    data: jobDetails,
    isLoading,
    isError,
    error,
  } = useSalesJobDetails(requestedDisplayJobId);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedDisplayJobId = normalizeSalesJobId(inputValue);

    if (!normalizedDisplayJobId) {
      setSearchParams({});
      return;
    }

    setSearchParams({ jobId: normalizedDisplayJobId });
  };

  return (
    <main className="min-h-screen bg-[#F6F8FC] px-4 py-8 text-[#0F172A] sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-6xl flex-col gap-6">
        <section className="rounded-[28px] bg-white px-6 py-7 shadow-[0_20px_60px_rgba(15,23,42,0.08)] sm:px-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="text-sm font-medium uppercase tracking-[0.24em] text-blue-600">
                Job Query
              </p>
              <h1 className="mt-3 text-3xl font-semibold tracking-[-0.03em] text-slate-900">
                Look up a job using its Job ID.
              </h1>
              <p className="mt-3 text-sm text-slate-500 sm:text-base">
                Enter the Job ID such as `JR12345678` to fetch the job details
                quickly.
              </p>
            </div>

            <Link
              to={SALES_DASHBOARD_PATH}
              className="inline-flex items-center justify-center rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              Back to sales dashboard
            </Link>
          </div>

          <form
            onSubmit={handleSubmit}
            className="mt-6 flex flex-col gap-3 sm:flex-row"
          >
            <input
              type="text"
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              placeholder="Enter Job ID"
              className="min-w-0 flex-1 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:bg-white"
            />
            <button
              type="submit"
              className="inline-flex items-center justify-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              Fetch job details
            </button>
          </form>
        </section>

        {!requestedDisplayJobId ? (
          <Card className="rounded-[24px] border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500 shadow-none">
            Submit a Job ID to load the job details here.
          </Card>
        ) : null}

        {requestedDisplayJobId && isLoading ? (
          <Card className="rounded-[24px] border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
            Loading job details...
          </Card>
        ) : null}

        {requestedDisplayJobId && isError ? (
          <Card className="rounded-[24px] border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700 shadow-none">
            {getQueryErrorMessage(error)}
          </Card>
        ) : null}

        {requestedDisplayJobId && !isLoading && !isError && jobDetails ? (
          <SalesJobDetailsPanel
            jobDetails={jobDetails}
            requestedDisplayJobId={requestedDisplayJobId}
          />
        ) : null}
      </div>
    </main>
  );
};

export default SalesJobLookupPage;
